import type { KeyboardEvent } from 'react';

/**
 * Stop a form from saving because something pressed Enter in a text field.
 *
 * A hardware barcode scanner is a keyboard: it types the code and then sends
 * Enter. Inside a `<form>` that has a submit button, that Enter *is* the submit
 * button — HTML calls it implicit submission — so scanning a code into a
 * barcode field saved the record and navigated away mid-edit. The same trap
 * catches anyone who fills a field and hits Enter out of habit.
 *
 * Hang this on the form's `onKeyDown`. It swallows Enter only where Enter's
 * one and only effect would have been to submit:
 *
 * - a child that already dealt with Enter called `preventDefault` first, and is
 *   left alone — the add-a-code fields, the combobox
 * - `textarea` and contenteditable keep Enter for newlines
 * - a focused button keeps it, **including the submit button**, so deliberate
 *   keyboard submitting still works; only the accidental kind goes
 * - file/submit/reset/button/image inputs keep their native Enter action
 * - an IME mid-composition is committing a candidate, not submitting
 *
 * Note this does not stop propagation, so a parent still sees the keystroke.
 */
export function blockImplicitSubmit(e: KeyboardEvent<HTMLElement>): void {
  if (e.key !== 'Enter' || e.defaultPrevented) return;
  if (e.nativeEvent.isComposing) return;

  const el = e.target as HTMLElement | null;
  if (!el || el.tagName !== 'INPUT') return;

  // Enter has real native work to do on these; on every other input type its
  // only effect is the submit we are here to prevent.
  const type = (el as HTMLInputElement).type;
  if (type === 'file' || type === 'submit' || type === 'reset' || type === 'button' || type === 'image') return;

  e.preventDefault();
}
